<template>
<div class="full-f-chart virtual-map-tree">
  <div class="zoom-button-group">
    <el-button size="mini" @click="onZoom('zoomIn')">
      <i class="far fa-search-plus" style="font-size: 14px;"></i>
    </el-button>
    <el-button size="mini" @click="onZoom('zoomOut')">
      <i class="far fa-search-minus" style="font-size: 14px;"></i>
    </el-button>
    <el-button size="mini" @click="onZoom('zoomInit')">
      <i class="far fa-sync-alt" style="font-size: 14px;"></i>
    </el-button>
    <el-button v-show="!fullscreenFlag" size="mini" @click="onFullScreenClick">
      <i class="fas fa-desktop" style="font-size: 14px;"></i>
    </el-button>
  </div>
  <div class="search-area">
    <el-input size="small" v-model="searchKeyword" clearable placeholder="검색 키워드를 입력해주십시오." @keyup.enter.native="searchNode(searchKeyword)" @clear="searchNode('')">
      <i class="el-input__icon el-icon-search" slot="suffix" style="cursor: pointer" @click="searchNode(searchKeyword)"></i>
    </el-input>
  </div>
  <div :class="svgWrapperDivClassName">
  </div>
</div>
</template>

<script>
import * as d3 from 'd3';
import { isEmpty } from '@/utils/basic';
import { routePathByObjId } from '@/utils/router';
import { menuDefine } from '@/constants/menu';
import { treeType } from '@/constants/tree';
import messageUtil from '@/utils/message';

export default {
  name: 'WVirtualMap',
  components: {
  },
  props: {
    active: {
      type: Boolean,
      defulat: () => false,
    },
    fullscreenFlag: {
      type: Boolean,
      defulat: () => false,
    },
  },
  data() {
    return {
      vmsTreeData: [],
      nodesByDepth: {},
      /* zoom */
      zoom: {
        x: 0, // Translate를 위한 X좌표 초기값
        y: 0, // Translate를 위한 Y좌표 초기값
        k: 1, // Scale 초기값
      },
      scale: 1,
      currentZoom: null,
      svgWrapperDivClassName: 'svg-wrapper-div',
      searchKeyword: '',
      clickedTextNode: null,
      root: null,

      /* 하이라이팅 관련 */
      highlightedTextDOMList: [],
      highlightedLinkDOMList: [],
      nullTimeout: null,
      lastHighlightStatus: null,
      highlightStatus: {
        search: null,
        click: null,
      },
      nullTime: 0,
    };
  },
  computed: {
    svgWrapperDiv() {
      return this.$el && d3.select(this.$el).select(`div[class="${this.svgWrapperDivClassName}"]`);
    },
  },
  methods: {
    drawVirtualMapTree(responseData) {
      this.initDatas('all');
      this.vmsTreeData = responseData;
      this.onDrawD3BtnClick();
    },
    onDrawD3BtnClick() {
      this.svgWrapperDiv.selectAll('*').remove();
      const nodesByDepth = {};
      const { vmsTreeData } = this;

      function iterateMakeNameProperty(newArray) {
        if (isEmpty(newArray)) {
          return;
        }
        newArray.forEach((value) => {
          value.label = value.label || 'noname';
          const { objtypeId } = value;
          if (!Object.prototype.hasOwnProperty.call(nodesByDepth, objtypeId)) {
            nodesByDepth[objtypeId] = [];
          }
          nodesByDepth[objtypeId].push(value);
          if (value.children) {
            iterateMakeNameProperty(value.children);
          }
        });
      }
      iterateMakeNameProperty(vmsTreeData.children);
      this.nodesByDepth = nodesByDepth;

      const svg = this.svgWrapperDiv.append('svg').attr('width', '960').attr('height', '900')
        .style('width', '100%')
        .style('height', '100%')
        .style('padding', '10px')
        .style('box-sizing', 'border-box');

      const width = +svg.attr('width');
      const g = svg.append('g')
        .attr('transform', 'translate(0, 0) scale(1)');


      const tree = ((treeData) => d3.tree()
        .size([360, (width / 2) - 120])
        .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth)(d3.hierarchy(treeData)));

      const root = tree(vmsTreeData);
      this.root = root;

      function project(x, y) {
        const angle = ((x - 90) / 180) * Math.PI;
        const radius = y;
        return [radius * Math.cos(angle), radius * Math.sin(angle)];
      }

      g.selectAll('.link')
        .data(root.descendants().slice(1))
        .enter()
        .append('path')
        .attr('class', 'link')
        .attr('d', ((d) => (
          `M${project(d.x, d.y)}
            C${project(d.x, (d.y + d.parent.y) / 2)}
             ${project(d.parent.x, (d.y + d.parent.y) / 2)}
             ${project(d.parent.x, d.parent.y)}`
        )));

      g.selectAll('.link')
        .data(root.links())
        .each(function annonymousFunction(d) {
          if (d.target) {
            d.target.linkNode = this;
          }
        })
        .style('cursor', 'default');

      const node = g.selectAll('.node')
        .data(root.descendants())
        .enter()
        .append('g')
        .each(function annonymousFunction(d) {
          d.gElement = this;
        })
        .attr('class', ((d) => `node${d.children ? ' node--internal' : ' node--leaf'}`))
        .attr('transform', (d) => `translate(${project(d.x, d.y)})`)
        .style('cursor', 'pointer');


      node.append('circle')
        .attr('fill', (d) => (d.data.eventlevelColorBg ? d.data.eventlevelColorBg : '#999'))
        .attr('r', 2.5);

      const me = this;
      let timeout = null;
      function mouseovered(active, delayTime) {
        return function annonymousFunction(mouseoveredNode) {
          if (timeout) {
            clearTimeout(timeout);
            timeout = null;
          }
          if (me.nullTimeout) {
            clearTimeout(me.nullTimeout);
            me.nullTimeout = null;
          }
          timeout = setTimeout(() => {
            const { objtypeId, datastoreVmsObjId } = mouseoveredNode.data;
            if (objtypeId === 'VVM') {
              const dataStoreNode = me.root.descendants().find((allNode) => datastoreVmsObjId === (allNode.data && allNode.data.vmsObjId));
              if (dataStoreNode) {
                me.highlightedTextDOMList.push(dataStoreNode.textNode);
              }
            }

            function iterateFunction(newNode) {
              me.highlightedTextDOMList.push(newNode.textNode);
              me.highlightedLinkDOMList.push(newNode.linkNode);
              if (newNode.parent) iterateFunction(newNode.parent);
            }
            iterateFunction(mouseoveredNode);


            me.highlightDomList(active);

            if (active === false) {
              me.nullTimeout = setTimeout(() => {
                if (!isEmpty(me.lastHighlightStatus)) {
                  const newStatus = me.highlightStatus[me.lastHighlightStatus];
                  // search가 올지 click이 올지 알 수 없기 때문에 예외처리 코드 삽입
                  if (!isEmpty(newStatus)) {
                    me.highlightedTextDOMList = newStatus.text;
                    me.highlightedLinkDOMList = newStatus.link;
                    me.highlightDomList(true);
                  }
                } else {
                  me.highlightDomList(false);
                }
              }, me.nullTime);
            }
          }, delayTime);
        };
      }


      node.append('text')
        .attr('class', 'text')
        .attr('dy', '0.31em')
        .attr('x', (d) => (((d.x < 180) === (!d.children)) ? 6 : -6))
        .attr('text-anchor', (d) => (((d.x < 180) === (!d.children)) ? 'start' : 'end'))
        .attr('transform', ((d) => {
          if (d.data.objtypeId === 'VVM') {
            return `rotate(${(d.x < 180) ? (d.x - 90) : (d.x + 90)})`;
          }
          return '';
        }))
        .text((d) => d.data.label)
        .each(function annonymousFunction(d) {
          d.textNode = this;
        })
        .style('fill', (d) => (d.data.eventlevelColorBg ? d.data.eventlevelColorBg : ''))
        .on('mouseover', mouseovered(true, 100))
        .on('mouseout', mouseovered(false, 0))
        .on('click', (d) => me.clickFunction(d))
        .on('dblclick', (d) => me.dblclickFunction(d));

      const box = g.node().getBBox();

      svg.remove()
        .attr('width', box.width)
        .attr('height', box.height)
        .attr('viewBox', `${box.x} ${box.y} ${box.width} ${box.height}`);

      this.svgWrapperDiv.node().appendChild(svg.node());

      this.svgWrapperDiv.select('svg').selectAll('*').style('opacity', 0);
      this.svgWrapperDiv.select('svg').selectAll('*')
        .transition()
        .duration(1000)
        .ease(d3.easeLinear)
        .style('opacity', 1)
        .transition()
        // opacity를 이용한 애니메이션 효과 적용 후 해당 속성을 삭제한다.
        // opacity 속성이 없어야 클래스를 이용한 스타일을 적용할 수 있다.
        .delay(500)
        .style('opacity', '');

      this.onZoom('zoomInit');
      svg.on('dblclick.zoom', null);
    },
    setZoom() {
      const zoom = d3.zoom().scaleExtent([0.5, 4]).on('zoom', () => {
        // 마우스 휠을 이용한 zoom기능 차단 처리
        // if (!d3.event.sourceEvent || d3.event.sourceEvent.type === 'mousemove')

        // 초기에 마우스 이동시 d3.event.transform: (0,0,1)로 설정됨 > x + 120 추가 (120,0,1)

        this.zoom = {
          x: d3.event.transform.x,
          y: d3.event.transform.y,
          k: d3.event.transform.k,
        };
        const currentG = this.svgWrapperDiv.select('svg > g');
        currentG.attr('transform', `translate(${this.zoom.x},${this.zoom.y}) scale(${this.zoom.k})`);
      });
      this.currentZoom = zoom;
      this.svgWrapperDiv.select('svg').call(zoom);
      return zoom;
    },
    onZoom(zoomType) {
      const svg = this.svgWrapperDiv.select('svg');
      const zoomListener = this.setZoom();

      if (zoomType === 'zoomIn') {
        svg.transition().call(zoomListener.scaleBy, 1.2);
      } else if (zoomType === 'zoomOut') {
        svg.transition().call(zoomListener.scaleBy, 0.8);
      } else if (zoomType === 'zoomInit') {
        svg.transition().call(zoomListener.transform, () => {
          this.zoom = { x: 0, y: 0, k: this.scale }; // zoomListener 에서 x: d3.event.transform.x + 120 설정하므로 x:0 으로 초기화
          return d3.zoomIdentity.translate(this.zoom.x, this.zoom.y).scale(this.zoom.k);
        });
      }
    },
    setTextLabelActive(flag, clickedNode) {
      // 이미 선택된 텍스트가 있다면 해당 스타일을 해제 한다.
      if (this.clickedTextNode) {
        d3.select(this.clickedTextData.textNode).classed('label--clicked', false);
      }
      // 새로 선택된 텍스트를 하이라이팅 한다.
      if (clickedNode && flag) {
        d3.select(clickedNode.textNode).classed('label--clicked', true);
        this.clickedTextNode = clickedNode;
      }
    },
    searchNode(searchKeyword) {
      const me = this;
      if (!isEmpty(searchKeyword)) {
        this.root.each((node) => {
          const searchValue = searchKeyword !== '' ? node.data.label : '';
          const searchResult = searchValue.toLowerCase().indexOf(searchKeyword.toLowerCase()) > -1;
          if (searchResult) {
            this.highlightedTextDOMList.push(node.textNode);
          }
        });
        me.highlightStatus.search = {
          text: me.highlightedTextDOMList,
          link: me.highlightedLinkDOMList,
        };
        this.lastHighlightStatus = 'search';
        this.highlightDomList(true);
      } else {
        this.nullTimeout = setTimeout(() => {
          me.highlightStatus.search = null;
          if (!isEmpty(me.highlightStatus.click)) {
            this.lastHighlightStatus = 'click';
            const newStatus = me.highlightStatus.click;
            me.highlightedTextDOMList = newStatus.text;
            me.highlightedLinkDOMList = newStatus.link;
            me.highlightDomList(true);
          } else {
            this.lastHighlightStatus = null;
            me.highlightDomList(false);
          }
        }, me.nullTime);
      }
    },
    clickedNodeFromTimeLine(objId) {
      let selectedObj = null;
      this.root.each((d) => {
        if (d.data.objId === objId) {
          selectedObj = d;
        }
      });
      if (selectedObj) {
        this.initDatas();
        this.clickFunction(selectedObj);
      }
    },
    isThisNodeClickable(clickType, nodeData) {
      let isClickable = true;

      // 단일 클릭일 때는 VVC, VDC를 막고 더블 클릭일 때는 VVC만 막는다
      const clickBannedList = ['VVC'];
      if (clickType === 'click') {
        clickBannedList.push('VDC');
      }
      if (!Object.hasOwnProperty.call(nodeData, 'objtypeId') || clickBannedList.includes(nodeData.objtypeId)) {
        isClickable = false;
      } else if (Object.hasOwnProperty.call(nodeData, 'objAuthFlag') && nodeData.type === 'obj' && nodeData.objAuthFlag === false) {
        this.$message(messageUtil.getCustomResultMsgData('해당 장비에 대한 권한이 없습니다.'));
        isClickable = false;
      } else {
        isClickable = true;
      }
      return isClickable;
    },
    highlightDomList(flag, domType) {
      this.svgWrapperDiv.selectAll('.text').each((d) => {
        d3.select(d.textNode).classed('label--inactive', flag);
      });

      this.svgWrapperDiv.selectAll('.link').classed('link--active', false);

      if (domType === 'text') {
        this.highlightedTextDOMList.forEach((dom) => {
          d3.select(dom).classed('label--inactive', false);
          d3.select(dom).classed('label--active', flag);
        });
      } else if (domType === 'link') {
        this.highlightedLinkDOMList.forEach((dom) => {
          d3.select(dom).classed('link--active', flag);
        });
      } else {
        this.highlightedTextDOMList.forEach((dom) => {
          d3.select(dom).classed('label--inactive', false);
          d3.select(dom).classed('label--active', flag);
        });
        this.highlightedLinkDOMList.forEach((dom) => {
          d3.select(dom).classed('link--active', flag);
        });
      }

      this.initHighlightedList();
    },
    initHighlightedList() {
      this.highlightedTextDOMList = [];
      this.highlightedLinkDOMList = [];
    },
    changeShowTime(flag) {
      if (flag === true) {
        const me = this;
        me.nullTimeout = setTimeout(() => {
          if (!isEmpty(me.highlightStatus.search)) {
            me.highlightStatus.click = null;
            me.lastHighlightStatus = 'search';
            const newStatus = me.highlightStatus.search;
            me.highlightedTextDOMList = newStatus.text;
            me.highlightedLinkDOMList = newStatus.link;
            me.highlightDomList(true);
          } else {
            this.initDatas();
            me.highlightDomList(false);
          }
        }, me.nullTime);
      }
    },
    onFullScreenClick() {
      this.$emit('fullscreen-on');
    },
    clickFunction(clickedNode) {
      const nodeData = clickedNode.data;
      if (isEmpty(nodeData)) {
        return;
      }
      delete nodeData.children;

      const me = this;
      function iterateFunction(newNode) {
        me.highlightedTextDOMList.push(newNode.textNode);
        me.highlightedLinkDOMList.push(newNode.linkNode);
        if (newNode.parent) iterateFunction(newNode.parent);
      }

      // 이미 클릭한 노드가 있고 새로 클릭한 노드와 다른 노드일 때 하이라이팅을 변경 한다.
      if ((this.clickedTextNode && this.clickedTextNode.data.vmsObjId) !== clickedNode.data.vmsObjId) {
        this.clickedTextNode = clickedNode;
        const isClickable = this.isThisNodeClickable('click', nodeData);


        if (isClickable) {
          this.$emit('select-target', nodeData);

          iterateFunction(clickedNode);

          this.highlightStatus.click = {
            text: this.highlightedTextDOMList,
            link: this.highlightedLinkDOMList,
          };
          this.lastHighlightStatus = 'click';
          this.highlightDomList(true);
        }
      }
    },
    dblclickFunction(dblclickedNode) {
      const nodeData = dblclickedNode.data;
      const isClickable = this.isThisNodeClickable('dblclick', nodeData);
      if (isClickable) {
        this.initDatas('all');
        const tabName = (nodeData.objtypeId !== 'VVM') ? 'virtualmap' : 'summary';
        routePathByObjId(this, nodeData.objId, menuDefine.PERF_OPER, tabName, null, treeType.PHYSICAL);
      }
    },
    initDatas(flag) {
      if (flag === 'all') {
        this.vmsTreeData = [];
        this.nodesByDepth = {};
        this.searchKeyword = '';
        this.root = null;
      }
      this.clickedTextNode = null;
      this.highlightedTextDOMList = [];
      this.highlightedLinkDOMList = [];
      this.nullTimeout = null;
      this.lastHighlightStatus = null;
      this.highlightStatus = {
        search: null,
        click: null,
      };
    },
  },
};
</script>
<style lang="scss">
.full-f-chart.virtual-map-tree {
  height: calc(92vh - 139px);
  position: relative;

  .zoom-button-group {
    position: absolute;
    left: 0;
    padding: 15px;
  }

  .search-area {
    position: absolute;
    right: 0;
    padding: 15px;
    width: 240px;
  }
  .svg-wrapper-div {
    width: 100%;
    height: calc(100% - 50px);
    .text {
      font-size: 16px;
      font-weight: 600;
    }
    .link {
      fill: none;
      stroke: #555;
      stroke-opacity: 0.4;
      stroke-width: 1.5px;
    }
    .link--active {
      stroke: blue !important;
      stroke-width: 8px;
    }
    .link-extension--active {
      stroke-opacity: 1;
    }
    .label--active , .label--clicked , .search--active {
      font-size: 18px;
      font-weight: bold;
    }
    .label--inactive , .search--inactive {
      opacity: 0.3;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/performance/components/virtual/VirtualMapTree.vue